import { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import useBasketSection from './use-basket-section';
import useSavedAddresses from './use-saved-addresses';
import { BasketSections } from '~/lib/types/enums';
import { QuoteAddress, QuoteRequirementSection } from '~/lib/types/basket-types';
import { cleanAddressFields } from '~/lib/validation';

export default function useAutoApplySavedAddress(requirements: QuoteRequirementSection) {
  const { isAuthenticated } = useAuth();
  const origin = useBasketSection<QuoteAddress>({ name: BasketSections.ORIGIN });
  const { defaultAddress, loading } = useSavedAddresses(origin.data.country, '');
  const [processing, setProcessing] = useState(true);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    // Skip while loading or processed previously
    if (!processing || loading || origin.loading) {
      return;
    }

    // When default address is updated earlier, verify its retrieved correctly
    if (saved && origin.data.metadata?.type === 'SAVED') {
      setProcessing(false);
      return;
    }

    // Update the address when origin address is not set and user is authenticated
    if (
      isAuthenticated &&
      defaultAddress &&
      !origin.data.metadata?.type &&
      origin.data.country === defaultAddress.country?.iso3Code
    ) {
      const address: QuoteAddress = {
        ...origin.data,
        contactname: defaultAddress.name,
        email: defaultAddress.email,
        phone: defaultAddress.phone,
        postcode: defaultAddress.postcode,
        property:
          defaultAddress.property && defaultAddress.property.length > 0
            ? defaultAddress.property
            : defaultAddress.organisation,
        street: defaultAddress.street,
        town: defaultAddress.town,
        organisation: defaultAddress.organisation,
        county: defaultAddress.county,
        metadata: {
          ...origin.data.metadata,
          type: 'SAVED',
          id: defaultAddress.id,
          isDefaultSavedAddress: defaultAddress.isDefault
        }
      };

      setSaved(true);
      origin.update(cleanAddressFields(address, requirements));
    } else {
      setProcessing(false);
    }
  }, [
    isAuthenticated,
    defaultAddress,
    origin.data.country,
    origin.data.metadata?.type,
    requirements,
    processing,
    origin,
    saved,
    loading
  ]);

  return { processing };
}
